import React from 'react';
import { useLang } from '../../i18n/LanguageContext';

// Le CONTENU de l'avis (titre, commentaire, prénom, note) vient de la base :
// il est écrit par le client et n'est jamais traduit. Seul l'habillage passe
// par t().
const ReviewCard = ({ review }) => {
    const { rating, title, comment, authorName, createdAt } = review;
    const { t, lang } = useLang();

    const renderStars = (rating) => {
        return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
    };

    // La date suit la langue affichée plutôt qu'un « fr-FR » figé : un visiteur
    // néerlandophone lisait « 13 août 2026 » au milieu d'une page en NL.
    // En français le rendu est identique à avant ('fr' ≡ 'fr-FR' ici).
    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(lang, options);
    };

    return (
        <div className="bg-gradient-to-br from-white to-[#F7F4EC] rounded-[14px] p-6 shadow-[0_8px_24px_rgba(212,165,116,0.12)] border-2 border-[#4AA294]/20 transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] flex flex-col gap-4 relative overflow-hidden cursor-pointer hover:-translate-y-2 hover:shadow-[0_16px_40px_rgba(212,165,116,0.2)] hover:border-[#EDB82F] hover:bg-gradient-to-br hover:from-white hover:to-[#EDB82F]/[0.03] animate-[fadeIn_0.5s_ease-out] focus-visible:outline-[3px] focus-visible:outline-[#EDB82F] focus-visible:outline-offset-2 group before:content-[''] before:absolute before:top-0 before:right-0 before:w-[100px] before:h-[100px] before:bg-[radial-gradient(circle,rgba(255,215,0,0.1)_0%,transparent_70%)] before:rounded-full before:pointer-events-none">
            {/* Header: Stars and Date */}
            <div className="flex justify-between items-center relative z-10">
                <div className="text-[#EDB82F] text-2xl tracking-wide flex gap-0.5 group-hover:scale-105 transition-transform" aria-label={t('avis.carte.noteAria', { note: rating })}>
                    {renderStars(rating)}
                </div>
                <span className="text-[#2F7E72] text-sm font-medium">{formatDate(createdAt)}</span>
            </div>

            {/* Title */}
            <h3 className="text-lg font-bold text-[#33403B] m-0 leading-snug font-['Fredoka',_'Poppins',_sans-serif] relative z-10">{title}</h3>

            {/* Comment */}
            <p className="text-[#5A5A5A] leading-relaxed text-base m-0 flex-grow relative z-10">{comment}</p>

            {/* Footer: Author and Badge */}
            <div className="flex items-center justify-between gap-3 mt-2 pt-4 border-t border-[#4AA294]/20 relative z-10">
                <span className="font-bold text-[#33403B] text-sm font-['Fredoka',_'Poppins',_sans-serif]">{authorName || t('avis.carte.clientVerifie')}</span>
                <span className="text-[#10B981] bg-gradient-to-br from-[#10B981]/10 to-[#10B981]/5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide border border-[#10B981]/30 uppercase flex items-center gap-1.5 flex-shrink-0">{t('avis.carte.achatVerifie')}</span>
            </div>

            {/* Keyframes for animations */}
            <style>{`
                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(10px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes wiggle {
                    0%, 100% {
                        transform: scale(1.05) rotate(0deg);
                    }
                    25% {
                        transform: scale(1.05) rotate(2deg);
                    }
                    75% {
                        transform: scale(1.05) rotate(-2deg);
                    }
                }
            `}</style>
        </div>
    );
};

export default ReviewCard;
